import {BaseTileProvider} from './base-tile-provider';
import {MapType} from '../../enums/map-type';
import {SettingsService} from '../../services/settings.service';
import {MapProviderDef, LayerDef} from './provider-registry';

/**
 * A provider that is entirely described by its URL templates.
 *
 * Every national mapping agency here differs only in the string it wants and
 * how deep it goes, so they share this one class instead of getting a file
 * apiece. Providers that need a key, a signature or a quadkey still have
 * their own.
 */
export class TemplateTileProvider extends BaseTileProvider {

  constructor(private readonly def: MapProviderDef) {
    super();
    this.name = def.name;
    this.alt = def.name;
  }

  getMapProvider(): string {
    // BaseTileProvider initialises `name = this.getMapProvider()`, and a
    // base field initialiser runs inside super() -- before the parameter
    // property above is assigned. So this is called once with no def at
    // all, and the constructor sets the real name straight after.
    return this.def ? this.def.id : '';
  }

  getMaxZoom(): number {
    return this.layerFor().maxZoom;
  }

  getUrlTemplate(mapType?: MapType): string {
    return this.layerFor(mapType).url;
  }

  getUrl(x, y, zoom, mapType?: MapType): string {
    return this.getUrlTemplate(mapType)
      .replace('{z}', zoom)
      .replace('{x}', x)
      .replace('{y}', y);
  }

  /**
   * Hybrid and terrain have no separate imagery anywhere in this list, so
   * they resolve the same way any unlisted type does: hybrid follows
   * satellite, everything else falls back to the provider's default layer.
   */
  private layerFor(mapType?: MapType): LayerDef {
    const mt = mapType ?? SettingsService.settings.mapType;
    const wantsAerial = mt === MapType.SATELLITE || mt === MapType.HYBRID;
    if (wantsAerial) {
      const aerial = this.def.layers[MapType.SATELLITE];
      if (aerial) {
        return aerial;
      }
    }
    return this.def.layers[mt] ?? this.def.layers.default;
  }
}
